import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConfirmLockSequenceInput } from '@citibox-panel/api-client';

// The confirm-sequence WRITE («Confirmar secuencia»), which moves a code table's server puntero. The
// confirm fn is injected (DI, MSW-testable). retry: false — a WRITE that moves a puntero must never
// be silently re-sent, and last-write-wins makes a retry a second, blind move.
//
// Its invalidation set is DELIBERATELY NARROWER than its five siblings': the «Entrega» does not
// change when a puntero moves, so `delivery-detail`, `user-deliveries` and `box-status` stay
// untouched. Invalidating the detail would raise the orchestrator's whole-card skeleton and fold the
// carril shut under the agent the instant they confirm. The codes go by EXACT key — one query serves
// both carriles of this cerradura — and the box log by prefix, since it is keyed by box id and the
// move may be recorded there.
//
// The error path invalidates NOTHING here: the code-agnostic refetch lives with the banner it
// accompanies, in the component that owns the flow, so the two cannot drift apart.
export function useConfirmLockSequence(
  lockId: string,
  confirmLockSequence: (lockId: string, input: ConfirmLockSequenceInput) => Promise<void>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConfirmLockSequenceInput) => confirmLockSequence(lockId, input),
    retry: false,
    // Fire-and-forget: do NOT return the promise. Returning it makes TanStack await the refetch
    // before the mutation settles, which would hide the refetch skeleton the caller drives off the
    // query's own fetching state.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lock-codes', lockId] });
      void queryClient.invalidateQueries({ queryKey: ['locker-log'] });
    },
  });
}
